import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Cpu, CheckCircle, XCircle, AlertTriangle, Trash2, RefreshCw, Atom } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { fadeInUp } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SolverStatus() {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("classical");

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/solver/status`);
      setStatus(res.data);
    } catch { /* ignore */ }
  };

  const saveToken = async () => {
    if (!token.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/solver/dwave-token`, { token: token.trim() });
      toast.success("Quantum solver token configured!");
      setToken("");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save token");
    } finally {
      setSaving(false);
    }
  };

  const removeToken = async () => {
    try {
      await axios.delete(`${API}/solver/dwave-token`);
      toast.success("Quantum solver token removed");
      fetchStatus();
    } catch {
      toast.error("Failed to remove token");
    }
  };

  if (!status) return null;

  const quantumStatus = status.quantum_status || "not_configured";

  return (
    <motion.div {...fadeInUp} data-testid="solver-status-section">
      <Card className="card-themed">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Cpu className="w-5 h-5 text-[var(--qn-primary)]" />
            Solver Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tabs: Classical | Quantum */}
          <div className="flex gap-2 border-b border-[var(--qn-border)] pb-2">
            <button
              onClick={() => setActiveTab("classical")}
              data-testid="solver-tab-classical"
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                activeTab === "classical"
                  ? "bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border border-[var(--qn-primary)]/20 border-b-0"
                  : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
              }`}
            >
              <Cpu className="w-4 h-4 inline mr-1.5" />
              Classical (Active)
            </button>
            <button
              onClick={() => setActiveTab("quantum")}
              data-testid="solver-tab-quantum"
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                activeTab === "quantum"
                  ? "bg-[var(--qn-secondary-muted)] text-[var(--qn-secondary)] border border-[var(--qn-secondary)]/20 border-b-0"
                  : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
              }`}
            >
              <Atom className="w-4 h-4 inline mr-1.5" />
              Quantum (Optional)
            </button>
          </div>

          {/* Classical Tab */}
          {activeTab === "classical" && (
            <div className="space-y-4" data-testid="solver-classical-panel">
              <p className="text-sm text-[var(--qn-foreground-muted)]">
                Classical solvers use quantum-inspired algorithms (simulated annealing mirrors quantum annealing behavior) for near-optimal routing.
              </p>
              <div>
                <span className="text-sm text-[var(--qn-foreground-muted)] block mb-2">Available Classical Solvers</span>
                <div className="flex gap-1.5 flex-wrap">
                  <Badge variant="outline" className="text-sm py-0.5 px-2">
                    <Cpu className="w-3 h-3 mr-1 inline" />
                    Greedy (fastest, &lt; 10ms)
                  </Badge>
                  <Badge variant="outline" className="text-sm py-0.5 px-2">
                    <Cpu className="w-3 h-3 mr-1 inline" />
                    Simulated Annealing (optimal, 50-500ms)
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-[var(--qn-foreground-subtle)]">
                Classical solvers handle all workflow sizes efficiently. They are always available and require no external configuration.
              </p>
            </div>
          )}

          {/* Quantum Tab */}
          {activeTab === "quantum" && (
            <div className="space-y-4" data-testid="solver-quantum-panel">
              {/* Quantum Status */}
              <div className="flex items-center gap-3">
                <span className="text-base text-[var(--qn-foreground-muted)]">Quantum Solver:</span>
                {quantumStatus === "connected" ? (
                  <Badge className="bg-green-500/20 text-green-400 text-sm py-1 px-2.5" data-testid="quantum-status-badge">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Connected
                  </Badge>
                ) : quantumStatus === "error" ? (
                  <Badge className="bg-yellow-500/20 text-yellow-400 text-sm py-1 px-2.5" data-testid="quantum-status-badge">
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Connection Error
                  </Badge>
                ) : (
                  <Badge className="bg-gray-500/20 text-gray-400 text-sm py-1 px-2.5" data-testid="quantum-status-badge">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Not Configured
                  </Badge>
                )}
              </div>

              {/* Status message */}
              {status.quantum_message && (
                <p className="text-sm text-[var(--qn-foreground-muted)]" data-testid="quantum-status-message">
                  {status.quantum_message}
                </p>
              )}

              {/* Reconnect button for error state */}
              {quantumStatus === "error" && (
                <Button size="sm" variant="outline" onClick={fetchStatus} data-testid="quantum-reconnect-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reconnect
                </Button>
              )}

              {/* Not configured description */}
              {quantumStatus === "not_configured" && (
                <p className="text-sm text-[var(--qn-foreground-subtle)]">
                  Connect your quantum computing provider to enable quantum-enhanced optimization for complex workflows (10+ tasks).
                  Classical optimization handles all workflows efficiently — quantum enhancement provides additional optimization power for very large combinatorial problems.
                </p>
              )}

              {/* Token Input */}
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="DEV-xxxx (D-Wave Leap API Token)"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="bg-[var(--qn-background)] border-[var(--qn-border)] text-base"
                  data-testid="quantum-token-input"
                />
                <Button size="sm" onClick={saveToken} disabled={saving || !token.trim()} className="btn-primary" data-testid="save-quantum-token-btn">
                  {saving ? "..." : "Save"}
                </Button>
                {status.quantum_configured && (
                  <Button size="sm" variant="destructive" onClick={removeToken} data-testid="remove-quantum-token-btn">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
              <p className="text-sm text-[var(--qn-foreground-muted)]">
                Quantum Solver Provider (D-Wave Leap) — Get your token at{" "}
                <a href="https://cloud.dwavesys.com/leap/" target="_blank" rel="noreferrer" className="text-[var(--qn-primary)] underline">
                  cloud.dwavesys.com/leap
                </a>. Free tier: 1 min QPU/month.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
